import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { FormField, requiredString, SecondaryButton, TextareaField } from "common";
import { FormikProps } from "formik";
import React, { useRef } from "react";
import { useMutation } from "react-query";
import { Merchandise, MerchandiseFulfillmentPolicy, MerchandiseRefundPolicy, MerchandiseType } from "types";
import { boolean, object, string } from "yup";
import {
    getFulfillmentInitialValues,
    getMerchandiseFulfillmentPayload,
    MerchandiseFulfillmentFormValues,
} from "./merchandiseHelper";
import { api } from "@/api/api";
import { Form } from "@/components/Common/Form";
import { RadioInput } from "@/components/Common/RadioInput";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { PurchaserInformationSection } from "@/components/Product/GuestManagement/PurchaserInformationSection";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { MERCHANDISE_API_PATH, MERCHANDISE_CACHE_KEY } from "@/models/Merchandise";
import { updateCache } from "@/utils/updateCache";

const validationSchema = object().shape({
    type: requiredString(),
    localPickup: boolean(),
    shipping: boolean(),
    revenueRecognitionPolicy: requiredString(),
    fulfillmentPolicy: requiredString(),
    refundPolicy: requiredString(),
    customerNamePreference: requiredString(),
    terms: string(),
});

interface MerchandiseFulfillmentProps {
    merchandise: Merchandise;
}

export const MerchandiseFulfillment = ({ merchandise }: MerchandiseFulfillmentProps) => {
    const formikRef = useRef<FormikProps<MerchandiseFulfillmentFormValues>>(null);

    const { mutateAsync: saveFulfillment } = useMutation(async (values: MerchandiseFulfillmentFormValues) => {
        const payload = getMerchandiseFulfillmentPayload(values, merchandise.warePreference);
        const { data } = await api.put<Merchandise>(`${MERCHANDISE_API_PATH}/${merchandise?.id}`, payload);
        flash.success("Fulfillment saved successfully");
        updateCache(MERCHANDISE_CACHE_KEY, data);
        return data;
    });

    return (
        <XolaFormik
            initialValues={getFulfillmentInitialValues(merchandise)}
            validationSchema={validationSchema}
            innerRef={formikRef}
            onSubmit={async (values) => saveFulfillment(values)}
        >
            {({ isSubmitting, values, setFieldValue, handleReset }) => (
                <Form>
                    <ScrollToFieldError />
                    <ContentLayout>
                        <div className="max-w-3xl space-y-8">
                            <div className="space-y-4">
                                <h3>Fulfillment</h3>

                                <ProductTypeSection values={values} setFieldValue={setFieldValue} />
                                {values.type === MerchandiseType.PHYSICAL && (
                                    <PickupDeliverySection values={values} setFieldValue={setFieldValue} />
                                )}
                                <FulfillmentSection values={values} setFieldValue={setFieldValue} />

                                <PurchaserInformationSection
                                    layout="stacked"
                                    customerNamePreference={values.customerNamePreference}
                                    onChange={(value) => setFieldValue("customerNamePreference", value)}
                                />

                                <RefundableSection values={values} setFieldValue={setFieldValue} />

                                <div className="text-sm font-bold">
                                    Custom Terms and Conditions
                                    <Tooltip content="These terms will be displayed to the customer at checkout.">
                                        <CircleInfoIcon className="ml-1" />
                                    </Tooltip>
                                </div>
                                <FormField name="terms">
                                    <TextareaField name="terms" rows={4} />
                                </FormField>
                            </div>
                        </div>

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton isLoading={isSubmitting}>Save</SubmitButton>
                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>
                    </ContentLayout>
                </Form>
            )}
        </XolaFormik>
    );
};

interface SectionProps {
    values: MerchandiseFulfillmentFormValues;
    setFieldValue: FormikProps<MerchandiseFulfillmentFormValues>["setFieldValue"];
}

const ProductTypeSection = ({ values, setFieldValue }: SectionProps) => (
    <div className="space-y-2">
        <div className="text-sm font-bold">
            Product Type
            <Tooltip content="This will be displayed to the customer at checkout.">
                <CircleInfoIcon className="ml-1" />
            </Tooltip>
        </div>
        <div className="space-y-2">
            <RadioInput
                name="type"
                value={MerchandiseType.PHYSICAL}
                checked={values.type === MerchandiseType.PHYSICAL}
                onChange={() => setFieldValue("type", MerchandiseType.PHYSICAL)}
            >
                Physical product
            </RadioInput>
            <RadioInput
                name="type"
                value={MerchandiseType.DIGITAL}
                checked={values.type === MerchandiseType.DIGITAL}
                onChange={() => setFieldValue("type", MerchandiseType.DIGITAL)}
            >
                Digital product
            </RadioInput>
        </div>
    </div>
);

const PickupDeliverySection = ({ values, setFieldValue }: SectionProps) => (
    <div className="space-y-2">
        <div className="text-sm font-bold">
            Pickup & Delivery Options
            <Tooltip content="This will be displayed to the customer at checkout.">
                <CircleInfoIcon className="ml-1" />
            </Tooltip>
        </div>
        <div className="space-y-2">
            <div>
                <Switch.Group>
                    {/* TODO: XS-1409 will work on Physical delivery in future story */}
                    <Switch
                        disabled={values.type === MerchandiseType.PHYSICAL}
                        isChecked={values.type === MerchandiseType.PHYSICAL || values.localPickup}
                        onChange={(checked: boolean) => setFieldValue("localPickup", checked)}
                    />
                    <Switch.Label className="ml-3">Available for local pickup</Switch.Label>
                </Switch.Group>
            </div>
            <div>
                <Switch.Group>
                    <Switch
                        disabled
                        isChecked={values.shipping}
                        onChange={(checked: boolean) => setFieldValue("shipping", checked)}
                    />
                    <Switch.Label className="ml-3">Available for shipping (not supported)</Switch.Label>
                </Switch.Group>
            </div>
        </div>
    </div>
);

const RefundableSection = ({ values, setFieldValue }: SectionProps) => (
    <div className="space-y-2">
        <div className="text-sm font-bold">
            Refundable{" "}
            <Tooltip content="This will be displayed to the customer at checkout. It does not limit the seller's ability to provide refunds.">
                <CircleInfoIcon />
            </Tooltip>
        </div>
        <Switch.Group>
            <Switch
                isChecked={values.refundPolicy === MerchandiseRefundPolicy.REFUND_ALLOWED}
                onChange={(checked: boolean) =>
                    setFieldValue(
                        "refundPolicy",
                        checked ? MerchandiseRefundPolicy.REFUND_ALLOWED : MerchandiseRefundPolicy.REFUND_NOT_ALLOWED,
                    )
                }
            />
            <div className="ml-3 flex items-center">
                <Switch.Label>This item can be returned for a refund</Switch.Label>
            </div>
        </Switch.Group>
    </div>
);

const FulfillmentSection = ({ values, setFieldValue }: SectionProps) => (
    <div className="space-y-2">
        <h6 className="font-bold">Purchase Fulfillment</h6>
        <Switch.Group>
            <Switch
                isChecked={values.fulfillmentPolicy === MerchandiseFulfillmentPolicy.IMMEDIATE}
                onChange={(checked: boolean) =>
                    setFieldValue(
                        "fulfillmentPolicy",
                        checked ? MerchandiseFulfillmentPolicy.IMMEDIATE : MerchandiseFulfillmentPolicy.MANUAL,
                    )
                }
            />
            <Switch.Label className="ml-3">
                Automatically mark back office orders as fulfilled at the time of purchase
            </Switch.Label>
        </Switch.Group>
        {/* XS-1409, X2-13638 - Auto fulfillment is not yet supported - will be enabled in future release        */}
        {/* <div className="flex  space-x-1">
            <span>Revenue is recognized upon</span>
            <InlineValuePopover
                isOpen={revenueRecognizedAtPopover.isVisible}
                text={
                    <span>
                        {values.revenueRecognitionPolicy === RevenueRecognitionPolicy.CREATION
                            ? "Purchase"
                            : "Fulfillment"}
                    </span>
                }
                onClick={revenueRecognizedAtPopover.toggle}
                onClickOutside={revenueRecognizedAtPopover.hide}
            >
                <PopoverList.Item
                    name={RevenueRecognitionPolicy.CREATION}
                    onClickItem={() => {
                        setFieldValue("revenueRecognitionPolicy", RevenueRecognitionPolicy.CREATION);
                        revenueRecognizedAtPopover.hide();
                    }}
                >
                    Purchase
                </PopoverList.Item>
                <PopoverList.Item
                    name={RevenueRecognitionPolicy.FULFILLMENT}
                    onClickItem={() => {
                        setFieldValue("revenueRecognitionPolicy", RevenueRecognitionPolicy.FULFILLMENT);
                        revenueRecognizedAtPopover.hide();
                    }}
                >
                    Fulfillment
                </PopoverList.Item>
            </InlineValuePopover>
        </div> */}
    </div>
);
